<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4"> Основная информация </v-card-title>
    <v-card-text>
      <div>
        <CardRow>
          <v-checkbox
            v-model="journalEditorialBoard.commonWork.isEffContract"
            class="rounded-lg mini-text field-edit d-inline-block"
            label="Работа входит в эффективный контракт"
            hide-details
            dense
            :disabled="isShowMode"
            @change="updateJournalEditorialBoard"
          />
        </CardRow>

        <CardAutocompleteRow
          v-model="journalEditorialBoard.magazine"
          :items="magazineList.list"
          item-value="id"
          item-text="name"
          label="Наименование издания"
          placeholder="Введите не менее 3-х символов"
          hide-no-data
          :disabled="isShowMode"
          :required="!isShowMode"
          :loading="magazineListLoading"
          :error-messages="errorMessages.magazine"
          @searchData="getMagazineList"
          @input="
            $v.journalEditorialBoard.magazine.$touch()
            updateJournalEditorialBoard()
          "
        />
        <CardRow>
          <v-checkbox
            v-model="journalEditorialBoard.listedByWAK"
            class="rounded-lg mini-text field-edit d-inline-block"
            label="Входит в перечень ВАК"
            hide-details
            dense
            :disabled="isShowMode"
            @change="updateJournalEditorialBoard"
          />
        </CardRow>

        <CardAutocompleteRow
          v-if="journalEditorialBoard.listedByWAK"
          v-model="journalEditorialBoard.catalog"
          :items="catalogList"
          item-text="name"
          item-value="id"
          label="Категорирование"
          :return-object="true"
          :error-messages="errorMessages.catalog"
          :disabled="isShowMode"
          :required="!isShowMode && journalEditorialBoard.listedByWAK"
          @input="
            $v.journalEditorialBoard.catalog.$touch()
            updateJournalEditorialBoard()
          "
        />
        <CardAutocompleteRow
          v-model="journalEditorialBoard.bibliography"
          :items="bibliographyList"
          item-text="name"
          item-value="id"
          label="База данных"
          placeholder="Выберите базу данных"
          :return-object="true"
          :disabled="isShowMode"
          :required="!isShowMode"
          :error-messages="errorMessages.bibliography"
          @input="
            $v.journalEditorialBoard.bibliography.$touch()
            updateJournalEditorialBoard()
          "
        />
        <CardAutocompleteRow
          v-model="journalEditorialBoard.editorialBoardRole"
          :items="editorialBoardRoleList"
          item-text="name"
          item-value="id"
          label="Роль в редакционной коллегии"
          placeholder="Выберите роль в редакционной коллегии"
          :return-object="true"
          :disabled="isShowMode"
          :required="!isShowMode"
          :error-messages="errorMessages.editorialBoardRole"
          @input="
            $v.journalEditorialBoard.editorialBoardRole.$touch()
            updateJournalEditorialBoard()
          "
        />
        <CardFieldRow
          v-model="journalEditorialBoard.linkToEditorialBoardPage"
          label="Ссылка на страницу редколлегии"
          placeholder="Добавьте ссылку на редколлегию"
          hide-border-bottom
          :disabled="isShowMode"
          :required="!isShowMode"
          :error-messages="errorMessages.linkToEditorialBoardPage"
          @input="
            $v.journalEditorialBoard.linkToEditorialBoardPage.$touch()
            updateJournalEditorialBoard()
          "
        />
        <CardTextAreaRow
          v-model="journalEditorialBoard.remark"
          label="Примечание"
          placeholder="Добавьте примечание"
          :disabled="isShowMode"
          @input="updateJournalEditorialBoard"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import CardAutocompleteRow from '@/components/ui/row/CardAutocompleteRow'
import CardFieldRow from '@/components/ui/row/CardFieldRow'
import CardTextAreaRow from '@/components/ui/row/CardTextAreaRow'
import CardRow from '@/components/ui/row/CardRow'
import _debounce from 'lodash/debounce'
import { MagazineParent } from '@/models'
import { magazineApi } from '@/api'
import { mapGetters, mapActions } from 'vuex'
import { dictionaryNames } from '@/scripts/consts'
import { setValidateMessages } from '@/scripts/validation'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { JournalEditorialBoard } from '../../../models/JournalEditorialBoards'
export default {
  name: 'MainInformationCard',
  props: {
    isShowMode: Boolean,
    selectedItem: Object
  },
  data: () => ({
    journalEditorialBoard: new JournalEditorialBoard(),
    magazineList: new MagazineParent(),
    magazineListLoading: false
  }),
  async mounted() {
    await this.getDictionaries([
      dictionaryNames.Catalog,
      dictionaryNames.Bibliography,
      dictionaryNames.EditorialBoardRole
    ])
  },
  validations() {
    return {
      journalEditorialBoard: {
        magazine: { required },
        catalog: { required: requiredIf(item => item.listedByWAK) },
        bibliography: { required },
        editorialBoardRole: { required },
        linkToEditorialBoardPage: { required }
      }
    }
  },
  components: {
    CardFieldRow,
    CardAutocompleteRow,
    CardRow,
    CardTextAreaRow
  },
  computed: {
    ...mapGetters('dictionaries', ['catalogList', 'bibliographyList', 'editorialBoardRoleList']),

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.journalEditorialBoard.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.journalEditorialBoard[key])
      })

      return allErrors
    }
  },
  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),

    updateJournalEditorialBoard() {
      this.$emit('update:selected-item', new JournalEditorialBoard(this.journalEditorialBoard.getForAPI()))
      this.updateInvalid()
    },
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    getMagazineList: _debounce(async function (val) {
      try {
        if (!val) {
          this.magazineList = new MagazineParent()
          this.magazineListLoading = false
          return
        }

        if (val?.length >= 3 && this.publication?.magazine?.name !== val) {
          this.magazineListLoading = true
          const data = await magazineApi.findAllByName(val)
          this.magazineList = new MagazineParent(data)
          if (this.journalEditorialBoard.magazine) this.magazineList.add(this.journalEditorialBoard.magazine)
        }
        this.magazineListLoading = false
      } catch (e) {
        console.log(e)
        if (e) {
          this.magazineListLoading = false
          this.showErrorMessage(e, 'Не удалось получить список журналов')
        } else this.magazineListLoading = true
      }
    }, 500)
  },
  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.selectedItem?.magazine) this.magazineList.add(this.selectedItem.magazine)
        this.journalEditorialBoard = new JournalEditorialBoard(val.getForAPI())
        if (this.$route.params.id == 0) {
          this.journalEditorialBoard.commonWork.isEffContract = true
        }
        this.updateInvalid()
      }
    }
  }
}
</script>
